import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiPlayCircle, FiCheckCircle, FiClock, FiPlusCircle, FiChevronRight, FiEdit2, FiTrash2, FiX, FiSave } from 'react-icons/fi';
import api from '../../utils/api';
import '../../styles/mobileResponsive.css';

const font = "'Outfit', sans-serif";
const C = { text: '#0f172a', sub: '#64748b', border: '#bae6fd', bg: '#f0f9ff', white: '#fff', accent: '#0ea5e9' };

const StatusBadge = ({ s }) => {
    const conf = {
        scheduled: { bg: '#fef3c7', c: '#d97706', label: 'Scheduled' },
        active:    { bg: '#d1fae5', c: '#059669', label: '🔴 LIVE' },
        ended:     { bg: '#f1f5f9', c: '#64748b', label: 'Ended' }
    };
    const t = conf[s] || conf.scheduled;
    return <span style={{ background: t.bg, color: t.c, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{t.label}</span>;
};

const Input = ({ label, ...props }) => (
    <div>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 5 }}>{label}</label>
        <input {...props} style={{ width: '100%', padding: '9px 13px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
    </div>
);

const ExamSessions = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [schedules, setSchedules] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [editSession, setEditSession] = useState(null);   // session being edited
    const [form, setForm] = useState({ schedule_id: '', subject_code: '', subject_name: '', exam_date: '', exam_time: '', department: '', semester: '', batch_name: '' });
    const [msg, setMsg] = useState('');
    const [filter, setFilter] = useState('all');
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [listSearch, setListSearch] = useState('');

    const load = async () => {
        try {
            const [sRes, schRes] = await Promise.all([
                api.get('/api/exam-controller/sessions'),
                api.get('/api/exam-controller/schedule-list')
            ]);
            setSessions(sRes.data.sessions);
            setSchedules(schRes.data.schedules);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleSchedulePick = (e) => {
        const id = e.target.value;
        const s = schedules.find(x => String(x.id) === id);
        if (s) setForm({ schedule_id: id, subject_code: s.subject_code, subject_name: s.subject_name, exam_date: s.exam_date?.slice(0, 10), exam_time: s.exam_time || '', department: s.department, semester: s.semester, batch_name: s.batch || '' });
    };

    const openCreate = () => {
        setEditSession(null);
        setForm({ schedule_id: '', subject_code: '', subject_name: '', exam_date: '', exam_time: '', department: '', semester: '', batch_name: '' });
        setMsg('');
        setSearchTerm('');
        setShowCreate(true);
    };

    const openEdit = (s) => {
        setEditSession(s);
        setForm({
            schedule_id: s.schedule_id || '',
            subject_code: s.subject_code || '',
            subject_name: s.subject_name || '',
            exam_date: s.exam_date?.slice(0, 10) || '',
            exam_time: s.exam_time || '',
            department: s.department || '',
            semester: s.semester || '',
            batch_name: s.batch_name || '',
        });
        setMsg('');
        setShowCreate(true);
    };

    const closeModal = () => { setShowCreate(false); setEditSession(null); setMsg(''); setSearchTerm(''); };

    const handleSubmit = async (e) => {
        e.preventDefault(); setMsg(''); setSubmitting(true);
        try {
            if (editSession) {
                await api.put(`/api/exam-controller/sessions/${editSession.id}`, form);
                setMsg('✅ Session updated!');
            } else {
                await api.post('/api/exam-controller/sessions', form);
                setMsg('✅ Session created!');
            }
            closeModal();
            load();
        } catch (err) { setMsg('❌ ' + (err.response?.data?.message || 'Failed')); }
        finally { setSubmitting(false); }
    };

    const startSession = async (id) => {
        if (!window.confirm('Start this exam session?')) return;
        try { await api.put(`/api/exam-controller/sessions/${id}/start`); load(); } catch { alert('Error starting session'); }
    };

    const endSession = async (id) => {
        if (!window.confirm('End this entire session? All halls will be closed.')) return;
        try { await api.put(`/api/exam-controller/sessions/${id}/end`); load(); } catch { alert('Error ending session'); }
    };

    const deleteSession = async (s) => {
        if (!window.confirm(`Delete session "${s.subject_name}"? This will also remove all hall assignments and seat allocations.`)) return;
        try {
            await api.delete(`/api/exam-controller/sessions/${s.id}`);
            load();
        } catch (err) { alert(err.response?.data?.message || 'Error deleting session'); }
    };

    const filtered = sessions.filter(s => {
        const matchesStatus = filter === 'all' || s.status === filter;
        const q = listSearch.toLowerCase();
        const matchesSearch = !listSearch || 
            s.subject_name?.toLowerCase().includes(q) || 
            s.subject_code?.toLowerCase().includes(q) || 
            s.department?.toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
    });

    return (
        <div style={{ fontFamily: font }}>
            {/* Header */}
            <div className="resp-page-header">
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: '0 0 4px' }}>Exam Sessions</h1>
                    <p style={{ color: C.sub, fontSize: 13, margin: 0 }}>Create, edit and control exam sessions. Assign halls and start exams.</p>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', width: '100%', maxWidth: 280, minWidth: 200 }}>
                        <input 
                            type="text" 
                            placeholder="🔍 Search sessions..." 
                            value={listSearch}
                            onChange={(e) => setListSearch(e.target.value)}
                            style={{ width: '100%', padding: '10px 15px', borderRadius: 12, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}
                        />
                        {listSearch && (
                            <button 
                                onClick={() => setListSearch('')} 
                                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 16, fontWeight: 800 }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, cursor: 'pointer', fontFamily: font, fontSize: 13, boxShadow: '0 4px 12px rgba(14,165,233,0.3)', justifyContent: 'center' }}>
                        <FiPlusCircle size={15} /> New Session
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="resp-grid-3" style={{ marginBottom: 20 }}>
                {[
                    { label: 'Scheduled', val: sessions.filter(s => s.status === 'scheduled').length, icon: <FiClock />, c: '#d97706', bg: '#fef3c7', key: 'scheduled' },
                    { label: 'Live Now',  val: sessions.filter(s => s.status === 'active').length,    icon: <FiPlayCircle />, c: '#059669', bg: '#d1fae5', key: 'active' },
                    { label: 'Ended',    val: sessions.filter(s => s.status === 'ended').length,     icon: <FiCheckCircle />, c: '#64748b', bg: '#f1f5f9', key: 'ended' }
                ].map((s, i) => (
                    <div key={i} onClick={() => setFilter(filter === s.key ? 'all' : s.key)} style={{ background: C.white, borderRadius: 16, border: `2px solid ${filter === s.key ? s.c : C.border}`, padding: '14px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: s.bg, color: s.c, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{s.icon}</div>
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 900, color: C.text }}>{s.val}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Create / Edit Modal */}
            {showCreate && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: C.white, borderRadius: 20, width: '100%', maxWidth: 560, boxShadow: '0 20px 40px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {editSession && <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800 }}>EDITING</span>}
                                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: C.text }}>{editSession ? 'Edit Session' : 'New Session'}</h2>
                            </div>
                            <button onClick={closeModal} style={{ background: '#f1f5f9', border: 'none', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.sub }}><FiX size={16} /></button>
                        </div>
                        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
                            {msg && <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 700, background: msg.startsWith('✅') ? '#d1fae5' : '#fee2e2', color: msg.startsWith('✅') ? '#065f46' : '#991b1b' }}>{msg}</div>}

                            {!editSession && (
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 5 }}>Pick from Exam Schedule</label>
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                        <div style={{ position: 'relative', flex: 1 }}>
                                            <input 
                                                type="text" 
                                                placeholder="🔍 Search subject, code or department..." 
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 12, outline: 'none', boxSizing: 'border-box', background: '#fff' }}
                                            />
                                            {searchTerm && (
                                                <button 
                                                    type="button" 
                                                    onClick={() => setSearchTerm('')} 
                                                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 14, fontWeight: 800 }}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <select value={form.schedule_id} onChange={handleSchedulePick} style={{ width: '100%', padding: '9px 13px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', background: C.bg, boxSizing: 'border-box' }}>
                                        <option value="">— Auto-fill from schedule —</option>
                                        {schedules
                                            .filter(s => {
                                                const search = searchTerm.toLowerCase();
                                                return !searchTerm || 
                                                    s.subject_name?.toLowerCase().includes(search) || 
                                                    s.subject_code?.toLowerCase().includes(search) ||
                                                    s.department?.toLowerCase().includes(search);
                                            })
                                            .map(s => <option key={s.id} value={s.id}>{s.subject_name} | {s.department} | {s.exam_date?.slice(0, 10)}</option>)
                                        }
                                    </select>
                                    {searchTerm && schedules.filter(s => {
                                        const search = searchTerm.toLowerCase();
                                        return s.subject_name?.toLowerCase().includes(search) || 
                                               s.subject_code?.toLowerCase().includes(search) ||
                                               s.department?.toLowerCase().includes(search);
                                    }).length === 0 && (
                                        <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, fontWeight: 600 }}>No matching schedules found.</div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                                <Input label="Subject Code *" required value={form.subject_code} onChange={e => setForm({ ...form, subject_code: e.target.value })} placeholder="e.g. CS-501" />
                                <Input label="Department" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="e.g. CSE" />
                                <div style={{ gridColumn: 'span 2' }}>
                                    <Input label="Subject Name *" required value={form.subject_name} onChange={e => setForm({ ...form, subject_name: e.target.value })} placeholder="e.g. Data Structures" />
                                </div>
                                <Input label="Exam Date *" required type="date" value={form.exam_date} onChange={e => setForm({ ...form, exam_date: e.target.value })} />
                                <Input label="Exam Time" value={form.exam_time} onChange={e => setForm({ ...form, exam_time: e.target.value })} placeholder="e.g. 09:00 AM - 12:00 PM" />
                                <Input label="Semester" value={form.semester} onChange={e => setForm({ ...form, semester: e.target.value })} placeholder="e.g. 5" />
                                <Input label="Batch" value={form.batch_name} onChange={e => setForm({ ...form, batch_name: e.target.value })} placeholder="e.g. BCA-2023-2027" />
                            </div>

                            <div style={{ display: 'flex', gap: 10 }}>
                                <button type="button" onClick={closeModal} style={{ flex: 1, padding: '11px', borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.bg, color: C.sub, fontWeight: 700, cursor: 'pointer', fontFamily: font, fontSize: 13 }}>Cancel</button>
                                <button type="submit" disabled={submitting} style={{ flex: 2, padding: '11px', borderRadius: 10, border: 'none', background: editSession ? '#0369a1' : 'linear-gradient(135deg,#38bdf8,#0ea5e9)', color: '#fff', fontWeight: 800, cursor: 'pointer', fontFamily: font, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                    <FiSave size={14} /> {submitting ? 'Saving...' : (editSession ? 'Update Session' : 'Create Session')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Sessions List */}
            <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: 14, color: C.text }}>{filter === 'all' ? 'All Sessions' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Sessions`} ({filtered.length})</span>
                    {filter !== 'all' && <button onClick={() => setFilter('all')} style={{ fontSize: 12, color: C.accent, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Clear Filter ×</button>}
                </div>

                {loading
                    ? <div style={{ padding: 40, textAlign: 'center', color: C.sub }}>Loading sessions...</div>
                    : filtered.length === 0
                        ? <div style={{ padding: 40, textAlign: 'center', color: C.sub, fontSize: 14 }}>{sessions.length === 0 ? 'No sessions yet. Click "New Session" to create one.' : `No ${filter} sessions.`}</div>
                        : filtered.map(s => (
                            <div key={s.id} className="resp-session-row">
                                <div style={{ flex: 1, minWidth: 160 }}>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 3 }}>
                                        {s.subject_name} <span style={{ color: C.sub, fontWeight: 600, fontSize: 12 }}>({s.subject_code})</span>
                                    </div>
                                    <div style={{ fontSize: 11, color: C.sub }}>
                                        {s.department} • Sem {s.semester} • {s.exam_date?.slice(0, 10)} {s.exam_time && `• ${s.exam_time}`}
                                    </div>
                                </div>

                                <div className="resp-session-actions">
                                    {s.hall_count > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                            <span style={{ fontSize: 12, color: '#0369a1', background: '#e0f2fe', padding: '4px 10px', borderRadius: 20, border: '1px solid #bae6fd', whiteSpace: 'nowrap', fontWeight: 700 }}>
                                                Already Assigned Hall ({s.hall_count})
                                            </span>
                                            {s.invigilator_names && (
                                                <span style={{ fontSize: 10, color: C.sub, fontWeight: 700, paddingLeft: 4 }}>
                                                    Invigilators: <span style={{ color: C.text }}>{s.invigilator_names}</span>
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span style={{ fontSize: 12, color: '#991b1b', background: '#fee2e2', padding: '4px 10px', borderRadius: 20, border: '1px solid #fecaca', whiteSpace: 'nowrap', fontWeight: 700 }}>
                                            Not Assigned Hall
                                        </span>
                                    )}
                                    <StatusBadge s={s.status} />

                                    {/* ── Edit — only for non-active sessions ── */}
                                    {s.status !== 'active' && (
                                        <button onClick={() => openEdit(s)} title="Edit session" style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#e0f2fe', color: '#0369a1', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <FiEdit2 size={12} /> Edit
                                        </button>
                                    )}

                                    {/* ── Session control buttons ── */}
                                    {s.status === 'scheduled' && (
                                        <>
                                            <button onClick={() => navigate(`/exam-controller/assign-halls?session=${s.id}`)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#e0f2fe', color: '#0284c7', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: font, whiteSpace: 'nowrap' }}>Assign Halls</button>
                                            <button onClick={() => startSession(s.id)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#d1fae5', color: '#059669', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: font }}>▶ Start</button>
                                        </>
                                    )}
                                    {s.status === 'active' && (
                                        <button onClick={() => endSession(s.id)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: font, whiteSpace: 'nowrap' }}>■ End</button>
                                    )}

                                    {/* ── View Halls ── */}
                                    <button onClick={() => navigate(`/exam-controller/assign-halls?session=${s.id}`)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: C.bg, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                                        <FiChevronRight size={16} />
                                    </button>

                                    {/* ── Delete — only for non-active sessions ── */}
                                    {s.status !== 'active' && (
                                        <button onClick={() => deleteSession(s)} title="Delete session" style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                                            <FiTrash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
            </div>
            {msg && !showCreate && <div style={{ padding: '10px 14px', background: msg.startsWith('✅') ? '#d1fae5' : '#fee2e2', borderRadius: 10, marginTop: 16, fontSize: 13, fontWeight: 700, color: msg.startsWith('✅') ? '#065f46' : '#991b1b' }}>{msg}</div>}
        </div>
    );
};

export default ExamSessions;
